import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { format, isValid, parseISO } from "date-fns";
import { enUS, nb } from "date-fns/locale";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { debounce } from "lodash";
import toast from "react-hot-toast";
import { pickingListsApi } from "../../../api/inventory/picking_lists";
import {
  quickPrintProductLabel,
  quickPrintDocumentLabel,
  quickPrintPackingSlip,
} from "../../../utils/quickPrintProductLabel";
import { getStorages } from "../../../slices/inventory/storage";
import {
  ArchiveBoxIcon,
  ChevronDownIcon,
  EnvelopeIcon,
  PrinterIcon,
  ArrowTopRightOnSquareIcon,
  DocumentTextIcon,
} from "@heroicons/react/24/outline";
import { CubeIcon } from "@heroicons/react/20/solid";

/** All module toggles in the UI. Orders stay available but are off by default (see DEFAULT_SELECTED_MODULE_KEYS). */
const MODULE_KEYS = ["order", "work_order", "report"];
const DEFAULT_SELECTED_MODULE_KEYS = ["work_order", "report"];
const LINE_EXIT_MS = 520;

function moduleRowAccent(sourceType) {
  switch (sourceType) {
    case "order":
      return "border-l-blue-500";
    case "work_order":
      return "border-l-violet-500";
    case "report":
      return "border-l-emerald-600";
    default:
      return "border-l-slate-400";
  }
}

function groupPickChipClass(pp) {
  if (!pp || !pp.lines_total) return "bg-slate-100 text-slate-700";
  const picked = Number(pp.lines_picked) || 0;
  const total = Number(pp.lines_total) || 0;
  if (picked >= total && total > 0) return "bg-emerald-100 text-emerald-800";
  if (picked > 0) return "bg-amber-100 text-amber-800";
  return "bg-slate-100 text-slate-700";
}

function adjustGroupPickProgress(groups, sourceType, sourceId, { pickedDelta = 0, totalDelta = 0 }) {
  return groups.map((g) => {
    if (g.source_type !== sourceType || Number(g.source_id) !== Number(sourceId)) return g;
    const pp = g.pick_progress;
    if (!pp) return g;
    const lt = Math.max(0, (Number(pp.lines_total) || 0) + totalDelta);
    let lp = (Number(pp.lines_picked) || 0) + pickedDelta;
    lp = Math.max(0, Math.min(lt, lp));
    return { ...g, pick_progress: { lines_total: lt, lines_picked: lp } };
  });
}

function moduleChipClass(sourceType) {
  switch (sourceType) {
    case "order":
      return "bg-blue-50 text-blue-900 ring-1 ring-blue-200";
    case "work_order":
      return "bg-violet-50 text-violet-900 ring-1 ring-violet-200";
    case "report":
      return "bg-emerald-50 text-emerald-900 ring-1 ring-emerald-200";
    default:
      return "bg-slate-100 text-slate-800 ring-1 ring-slate-200";
  }
}

function mergeLineIntoGroups(prev, lineId, serverLine) {
  return prev.map((g) => ({
    ...g,
    lines: (g.lines || []).map((l) => (l.id === lineId ? { ...l, ...serverLine } : l)),
  }));
}

function removeLineFromGroups(prev, lineId) {
  return prev
    .map((g) => ({
      ...g,
      lines: (g.lines || []).filter((l) => l.id !== lineId),
    }))
    .filter((g) => (g.lines?.length || 0) > 0);
}

function metaFromGroups(gs) {
  return {
    group_count: gs.length,
    line_count: gs.reduce((n, g) => n + (g.lines?.length || 0), 0),
  };
}

function lineStatusLabel(line, t) {
  if (line.invoiced) return t("inventory.picking.status.invoiced");
  const qUsed = line.quantity_used || 0;
  if (qUsed > 0 && !line.taken_in_vehicle) return t("inventory.picking.status.used_job");
  if (line.taken_in_vehicle) return t("inventory.picking.status.in_vehicle");
  if (line.not_used) return t("inventory.picking.status.not_used");
  if (line.returned_to_storage) return t("inventory.picking.status.returned");
  if (line.left_in_vehicle) return t("inventory.picking.status.left_vehicle");
  if (line.picked) return t("inventory.picking.status.picked");
  return t("inventory.picking.status.not_picked");
}

function moduleLabel(type, t) {
  switch (type) {
    case "order":
      return t("inventory.picking.module.order");
    case "work_order":
      return t("inventory.picking.module.work_order");
    case "report":
      return t("inventory.picking.module.report");
    default:
      return type;
  }
}

function sourcePath(sourceType, sourceId) {
  if (sourceType === "order") return `/dashboard/orders/${sourceId}`;
  if (sourceType === "work_order") return `/dashboard/work_orders/${sourceId}`;
  if (sourceType === "report") return `/dashboard/service/reports/${sourceId}`;
  return "/dashboard/inventory/picking-list";
}

function formatExecutionOn(iso, language) {
  if (!iso) return null;
  const d = parseISO(iso);
  if (!isValid(d)) return null;
  const loc = language && (language.startsWith("nb") || language.startsWith("no")) ? nb : enUS;
  return format(d, "PPP", { locale: loc });
}

const inputClass =
  "block w-full rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-200";

const selectClass =
  "block w-full rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 shadow-sm focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-200 sm:min-w-[12rem]";

export default function PickingListPage() {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const storagesState = useSelector((s) => s.inventory_storages);
  const storages = storagesState?.data || [];

  const [loading, setLoading] = useState(true);
  const [groups, setGroups] = useState([]);
  const [meta, setMeta] = useState({ group_count: 0, line_count: 0 });
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [modules, setModules] = useState(() => new Set(DEFAULT_SELECTED_MODULE_KEYS));
  const [unpickedOnly, setUnpickedOnly] = useState(true);
  const [warehouseOnly, setWarehouseOnly] = useState(false);
  const [includeSkipped, setIncludeSkipped] = useState(false);
  const [reminderSending, setReminderSending] = useState(false);
  const [lineBusy, setLineBusy] = useState({});
  const [lineStorage, setLineStorage] = useState({});
  /** 'pick' | 'skip' — row plays exit animation then is removed from local state */
  const [exitingLines, setExitingLines] = useState({});

  useEffect(() => {
    document.title = `${t("inventory.picking.title")} | Promang`;
  }, [t]);

  useEffect(() => {
    dispatch(getStorages());
  }, [dispatch]);

  const debouncedSetQuery = useMemo(
    () =>
      debounce((value) => {
        setDebouncedQuery(value);
      }, 300),
    []
  );

  useEffect(() => {
    debouncedSetQuery(query);
    return () => debouncedSetQuery.cancel();
  }, [query, debouncedSetQuery]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const modList = MODULE_KEYS.filter((k) => modules.has(k));
      const res = await pickingListsApi.getOverview({
        modules: modList.join(","),
        query: debouncedQuery,
        unpicked_only: unpickedOnly,
        warehouse_only: warehouseOnly,
        include_skipped: includeSkipped,
      });
      const payload = res?.data?.data;
      if (res?.data?.success && payload) {
        setGroups(payload.groups || []);
        setMeta(payload.meta || { group_count: 0, line_count: 0 });
      } else {
        setGroups([]);
        toast.error(t("inventory.picking.load_error"));
      }
    } catch (e) {
      console.error(e);
      setGroups([]);
      toast.error(t("inventory.picking.load_error"));
    } finally {
      setLoading(false);
    }
  }, [debouncedQuery, modules, unpickedOnly, warehouseOnly, includeSkipped, t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    setLineStorage((prev) => {
      const next = { ...prev };
      groups.forEach((g) => {
        g.lines?.forEach((line) => {
          if (line.storage_id != null && next[line.id] === undefined) {
            next[line.id] = line.storage_id;
          }
        });
      });
      return next;
    });
  }, [groups]);

  const toggleModule = (key) => {
    setModules((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      if (next.size === 0) next.add(key);
      return next;
    });
  };

  const scheduleRemoveLine = useCallback((lineId, mode, ctx) => {
    setExitingLines((prev) => ({ ...prev, [lineId]: mode }));
    window.setTimeout(() => {
      setGroups((prev) => {
        let next = removeLineFromGroups(prev, lineId);
        if (ctx && mode === "pick") {
          next = adjustGroupPickProgress(next, ctx.source_type, ctx.source_id, { pickedDelta: 1 });
        } else if (ctx && mode === "skip") {
          next = adjustGroupPickProgress(next, ctx.source_type, ctx.source_id, {
            totalDelta: -1,
            pickedDelta: ctx.line_was_picked ? -1 : 0,
          });
        }
        setMeta(metaFromGroups(next));
        return next;
      });
      setExitingLines((prev) => {
        const next = { ...prev };
        delete next[lineId];
        return next;
      });
      setLineBusy((b) => ({ ...b, [lineId]: false }));
    }, LINE_EXIT_MS);
  }, []);

  const onTogglePicked = async (line, picked, g) => {
    if (line.skip_picking) return;
    if (picked) {
      const sid = lineStorage[line.id] ?? line.storage_id;
      if (sid == null || sid === "") {
        toast.error(t("inventory.picking.storage_required"));
        return;
      }
    }

    setLineBusy((b) => ({ ...b, [line.id]: true }));
    let shouldHoldBusy = false;
    try {
      const payload = {
        order_item_id: line.id,
        picked,
      };
      if (picked) {
        payload.storage_id = lineStorage[line.id] ?? line.storage_id;
      }
      const res = await pickingListsApi.togglePicked(payload);
      if (res?.data?.success) {
        toast.success(picked ? t("inventory.picking.marked_picked") : t("inventory.picking.marked_unpicked"));
        if (picked && unpickedOnly) {
          shouldHoldBusy = true;
          scheduleRemoveLine(line.id, "pick", {
            source_type: g.source_type,
            source_id: g.source_id,
          });
        } else {
          const serverLine = res?.data?.data;
          if (serverLine) {
            setGroups((prev) => {
              let next = mergeLineIntoGroups(prev, line.id, serverLine);
              if (g?.pick_progress) {
                const wasPicked = Boolean(line.picked);
                const nowPicked = Boolean(serverLine.picked);
                if (nowPicked && !wasPicked) {
                  next = adjustGroupPickProgress(next, g.source_type, g.source_id, { pickedDelta: 1 });
                } else if (!nowPicked && wasPicked) {
                  next = adjustGroupPickProgress(next, g.source_type, g.source_id, { pickedDelta: -1 });
                }
              }
              return next;
            });
          }
        }
      } else {
        toast.error(res?.data?.errors?.join?.(", ") || t("inventory.picking.toggle_error"));
      }
    } catch (e) {
      console.error(e);
      toast.error(t("inventory.picking.toggle_error"));
    } finally {
      if (!shouldHoldBusy) {
        setLineBusy((b) => ({ ...b, [line.id]: false }));
      }
    }
  };

  const onToggleSkipPicking = async (line, skip, g) => {
    setLineBusy((b) => ({ ...b, [line.id]: true }));
    let shouldHoldBusy = false;
    try {
      const res = await pickingListsApi.toggleSkipPicking({
        order_item_id: line.id,
        skip_picking: skip,
      });
      if (res?.data?.success) {
        toast.success(skip ? t("inventory.picking.skip.on") : t("inventory.picking.skip.off"));
        if (skip && !includeSkipped) {
          shouldHoldBusy = true;
          scheduleRemoveLine(line.id, "skip", {
            source_type: g.source_type,
            source_id: g.source_id,
            line_was_picked: Boolean(line.picked),
          });
        } else {
          const serverLine = res?.data?.data;
          if (serverLine) {
            setGroups((prev) => {
              let next = mergeLineIntoGroups(prev, line.id, serverLine);
              if (g?.pick_progress) {
                const wasSkip = Boolean(line.skip_picking);
                const nowSkip = Boolean(serverLine.skip_picking);
                if (nowSkip && !wasSkip) {
                  next = adjustGroupPickProgress(next, g.source_type, g.source_id, {
                    totalDelta: -1,
                    pickedDelta: line.picked ? -1 : 0,
                  });
                } else if (!nowSkip && wasSkip) {
                  next = adjustGroupPickProgress(next, g.source_type, g.source_id, {
                    totalDelta: 1,
                    pickedDelta: serverLine.picked ? 1 : 0,
                  });
                }
              }
              return next;
            });
          }
        }
      } else {
        toast.error(res?.data?.errors?.join?.(", ") || t("inventory.picking.skip.error"));
      }
    } catch (e) {
      console.error(e);
      toast.error(t("inventory.picking.skip.error"));
    } finally {
      if (!shouldHoldBusy) {
        setLineBusy((b) => ({ ...b, [line.id]: false }));
      }
    }
  };

  const onSendReminder = async () => {
    setReminderSending(true);
    try {
      const res = await pickingListsApi.sendReminder();
      if (res?.data?.success) {
        toast.success(res?.data?.message || t("inventory.picking.reminder_sent"));
      } else {
        toast.error(res?.data?.errors?.join?.(", ") || t("inventory.picking.reminder_error"));
      }
    } catch (e) {
      console.error(e);
      const data = e?.response?.data;
      const msg = data?.errors?.join?.(", ") || data?.message;
      toast.error(msg || t("inventory.picking.reminder_error"));
    } finally {
      setReminderSending(false);
    }
  };

  const onQuickLabel = async (productId) => {
    try {
      await quickPrintProductLabel(productId, { t });
      toast.success(t("inventory.picking.quick_print.opened"));
    } catch (e) {
      console.error(e);
      toast.error(e?.message || t("inventory.picking.quick_print.failed"));
    }
  };

  const onQuickDocumentLabel = async (documentLabel, e) => {
    e?.stopPropagation?.();
    try {
      await quickPrintDocumentLabel(documentLabel, { t });
      toast.success(t("inventory.picking.quick_print.opened"));
    } catch (err) {
      console.error(err);
      toast.error(err?.message || t("inventory.picking.quick_print.failed"));
    }
  };

  const onPackingSlip = async (g, e) => {
    e?.stopPropagation?.();
    if (g.source_type !== "report" && g.source_type !== "work_order") return;
    try {
      await quickPrintPackingSlip({ sourceType: g.source_type, sourceId: g.source_id }, { t });
      toast.success(t("inventory.picking.delivery_note.opened"));
    } catch (err) {
      console.error(err);
      toast.error(err?.message || t("inventory.picking.delivery_note.failed"));
    }
  };

  const storageSelect = (line) => (
    <label className="block w-full sm:w-auto">
      <span className="mb-1 block text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
        {t("inventory.picking.pick_storage")}
      </span>
      <select
        className={selectClass}
        value={lineStorage[line.id] ?? line.storage_id ?? ""}
        onChange={(e) =>
          setLineStorage((s) => ({
            ...s,
            [line.id]: e.target.value === "" ? "" : Number(e.target.value),
          }))
        }
      >
        <option value="">{t("inventory.picking.storage_placeholder")}</option>
        {storages.map((s) => (
          <option key={s.id} value={s.id}>
            {s.storage_number ? `${s.storage_number} · ${s.title || ""}` : s.title || `#${s.id}`}
          </option>
        ))}
      </select>
    </label>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="mb-8 rounded-[2rem] border border-slate-200 bg-white px-6 py-6 shadow-sm">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-slate-600">
                <ArchiveBoxIcon className="h-4 w-4" />
                {t("inventory.picking.hero_kicker")}
              </div>
              <h1 className="text-3xl font-semibold tracking-tight text-slate-950">
                {t("inventory.picking.title")}
              </h1>
              <p className="mt-2 max-w-2xl text-sm text-slate-600">{t("inventory.picking.subtitle")}</p>
            </div>

            <div className="grid w-full max-w-xl grid-cols-2 gap-3 sm:max-w-none sm:grid-cols-4">
              <div className="rounded-2xl bg-slate-950 px-4 py-3 text-white">
                <div className="text-xs uppercase tracking-[0.18em] text-slate-300">
                  {t("inventory.picking.meta.groups")}
                </div>
                <div className="mt-2 text-2xl font-semibold">{meta.group_count ?? 0}</div>
              </div>
              <div className="rounded-2xl bg-slate-100 px-4 py-3">
                <div className="text-xs uppercase tracking-[0.18em] text-slate-500">
                  {t("inventory.picking.meta.lines")}
                </div>
                <div className="mt-2 text-2xl font-semibold text-slate-900">{meta.line_count ?? 0}</div>
              </div>
              <button
                type="button"
                onClick={onSendReminder}
                disabled={reminderSending}
                className="col-span-2 inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-900 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 sm:col-span-2"
              >
                <EnvelopeIcon className="h-5 w-5 shrink-0 text-slate-600" />
                {t("inventory.picking.send_reminder")}
              </button>
            </div>
          </div>
        </div>

        <section className="mb-8 overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-200 px-6 py-5">
            <h2 className="text-sm font-semibold uppercase tracking-[0.18em] text-slate-500">
              {t("inventory.picking.filters_heading")}
            </h2>
            <p className="mt-1 text-sm text-slate-600">
              {t("inventory.picking.filters_description")}
            </p>
          </div>
          <div className="space-y-5 px-6 py-6">
            <input
              type="search"
              className={`${inputClass} max-w-xl`}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("inventory.picking.search_placeholder")}
              autoComplete="off"
            />

            <div className="flex flex-wrap gap-2">
              {MODULE_KEYS.map((key) => {
                const on = modules.has(key);
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => toggleModule(key)}
                    className={
                      on
                        ? "rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800"
                        : "rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"
                    }
                  >
                    {moduleLabel(key, t)}
                  </button>
                );
              })}
            </div>

            <div className="flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-center">
              <label className="flex cursor-pointer items-center gap-3 text-sm text-slate-700">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-400"
                  checked={unpickedOnly}
                  onChange={(e) => setUnpickedOnly(e.target.checked)}
                />
                <span>{t("inventory.picking.filter.unpicked_only")}</span>
              </label>
              <label className="flex cursor-pointer items-center gap-3 text-sm text-slate-700">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-400"
                  checked={warehouseOnly}
                  onChange={(e) => setWarehouseOnly(e.target.checked)}
                />
                <span>{t("inventory.picking.filter.warehouse_only")}</span>
              </label>
              <label className="flex cursor-pointer items-center gap-3 text-sm text-slate-700">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-400"
                  checked={includeSkipped}
                  onChange={(e) => setIncludeSkipped(e.target.checked)}
                />
                <span>{t("inventory.picking.filter.include_skipped")}</span>
              </label>
              <button
                type="button"
                onClick={fetchData}
                disabled={loading}
                className="inline-flex items-center justify-center rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-400 hover:bg-white disabled:cursor-not-allowed disabled:opacity-50"
              >
                {t("inventory.picking.refresh")}
              </button>
            </div>
            <p className="max-w-2xl text-xs leading-relaxed text-slate-500">
              {t("inventory.picking.filter.warehouse_only_help")}
            </p>
            <p className="max-w-2xl text-xs leading-relaxed text-slate-500">
              {t("inventory.picking.filter.include_skipped_help")}
            </p>
          </div>
        </section>

        <section className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-200 px-6 py-6">
            <div className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-slate-600">
              <ArchiveBoxIcon className="h-4 w-4" />
              {t("inventory.picking.list_kicker")}
            </div>
            <h2 className="mt-3 text-2xl font-semibold tracking-tight text-slate-950">
              {t("inventory.picking.list_title")}
            </h2>
            <p className="mt-1 text-sm text-slate-600">
              {t("inventory.picking.list_description")}
            </p>
          </div>

          {loading ? (
            <div className="flex flex-col items-center justify-center gap-3 px-6 py-16">
              <div
                className="h-10 w-10 animate-spin rounded-full border-2 border-slate-200 border-t-slate-600"
                aria-hidden
              />
              <p className="text-sm text-slate-500">{t("inventory.picking.loading")}</p>
            </div>
          ) : null}

          {!loading && groups.length === 0 ? (
            <div className="px-6 py-12">
              <div className="rounded-3xl border border-dashed border-slate-300 bg-slate-50 px-6 py-10 text-center">
                <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-white text-slate-700 shadow-sm">
                  <ArchiveBoxIcon className="h-7 w-7" />
                </div>
                <h3 className="mt-4 text-lg font-semibold text-slate-900">
                  {t("inventory.picking.empty_title")}
                </h3>
                <p className="mt-2 text-sm text-slate-500">{t("inventory.picking.empty")}</p>
              </div>
            </div>
          ) : null}

          {!loading && groups.length > 0 ? (
            <div className="divide-y divide-slate-200">
              {groups.map((g) => (
                <details
                  key={`${g.source_type}-${g.source_id}`}
                  className="open:bg-white"
                  open
                >
                  <summary className="flex cursor-pointer list-none flex-col gap-3 bg-slate-50 px-4 py-4 transition marker:content-none hover:bg-slate-100 sm:px-6 [&::-webkit-details-marker]:hidden">
                    <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <div className="flex min-w-0 flex-1 items-start gap-3">
                        <span className="mt-0.5 inline-flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-2xl bg-white text-slate-700 shadow-sm ring-1 ring-slate-200">
                          <ChevronDownIcon className="h-5 w-5 text-slate-500" />
                        </span>
                        <div className="min-w-0">
                          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                            {moduleLabel(g.source_type, t)}
                          </div>
                          <div className="mt-1 text-lg font-semibold text-slate-950">{g.label}</div>
                          {g.subtitle ? (
                            <p className="mt-1 text-sm leading-6 text-slate-600">{g.subtitle}</p>
                          ) : null}
                          {(g.source_type === "work_order" || g.source_type === "report") &&
                          g.execution_on ? (
                            <p className="mt-2 text-sm font-medium text-slate-800">
                              {t("inventory.picking.execution_on")}:{" "}
                              {formatExecutionOn(g.execution_on, i18n.language)}
                            </p>
                          ) : null}
                          {g.pick_progress && g.pick_progress.lines_total > 0 ? (
                            <div
                              className={`mt-2 inline-flex max-w-full items-center gap-1 rounded-full px-2 py-1 text-xs font-medium ${groupPickChipClass(
                                g.pick_progress
                              )}`}
                              title={t("item_list_handling.pick_progress_tooltip", {
                                picked: g.pick_progress.lines_picked,
                                total: g.pick_progress.lines_total,
                                status: "—",
                              })}
                            >
                              <CubeIcon className="h-3 w-3 shrink-0" />
                              <span className="truncate">
                                {t("item_list_handling.pick_progress_short", {
                                  picked: g.pick_progress.lines_picked,
                                  total: g.pick_progress.lines_total,
                                })}
                              </span>
                            </div>
                          ) : null}
                        </div>
                      </div>
                      <div
                        className="flex flex-wrap items-center gap-2 sm:justify-end"
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                        role="presentation"
                      >
                        {g.document_label ? (
                          <button
                            type="button"
                            title={t("inventory.picking.quick_print.document_tooltip")}
                            onClick={(e) => onQuickDocumentLabel(g.document_label, e)}
                            className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50"
                          >
                            <PrinterIcon className="h-5 w-5" />
                          </button>
                        ) : null}
                        {(g.source_type === "report" || g.source_type === "work_order") && (
                          <button
                            type="button"
                            title={t("inventory.picking.delivery_note.tooltip")}
                            onClick={(e) => onPackingSlip(g, e)}
                            className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50"
                          >
                            <DocumentTextIcon className="h-5 w-5" />
                          </button>
                        )}
                        <Link
                          to={sourcePath(g.source_type, g.source_id)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-400 hover:bg-slate-50"
                        >
                          <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                          {t("inventory.picking.open_source")}
                        </Link>
                        {g.handling_item_list_id ? (
                          <Link
                            to={`/dashboard/projects/item-lists/${g.handling_item_list_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center justify-center rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-400 hover:bg-slate-50"
                          >
                            {t("inventory.picking.open_item_list")}
                          </Link>
                        ) : null}
                      </div>
                    </div>
                  </summary>

                  <div className="divide-y divide-slate-100 bg-white">
                    {g.lines.map((line) => {
                      const warehouseTracked =
                        line.warehouse_tracked === true ||
                        (line.product && line.product.warehouse_enabled === true);
                      const exitMode = exitingLines[line.id];
                      const isExitingPick = exitMode === "pick";
                      const isExitingSkip = exitMode === "skip";
                      const rowBusy = Boolean(lineBusy[line.id] || exitMode);
                      return (
                      <div
                        key={line.id}
                        className={`flex flex-col gap-0 border-l-4 px-4 py-5 transition-all duration-500 ease-in-out sm:px-6 ${moduleRowAccent(
                          g.source_type
                        )} ${
                          line.skip_picking
                            ? "bg-slate-100/90 ring-1 ring-inset ring-slate-200/80"
                            : warehouseTracked
                              ? "hover:bg-slate-50/80"
                              : "bg-amber-50/50 hover:bg-amber-50/80"
                        } ${
                          !warehouseTracked && !line.skip_picking ? "border-l-amber-400" : ""
                        } ${
                          isExitingPick
                            ? "!border-l-emerald-500 bg-emerald-100 opacity-0 -translate-y-1 pointer-events-none"
                            : ""
                        } ${
                          isExitingSkip
                            ? "!border-l-slate-400 bg-slate-200 opacity-0 -translate-y-1 pointer-events-none"
                            : ""
                        }`}
                      >
                        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                        <div className="min-w-0 flex-1">
                          <div className="mb-3 flex flex-wrap items-center gap-2 border-b border-slate-100 pb-3">
                            <span
                              className={`inline-flex shrink-0 items-center rounded-full px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wider ${moduleChipClass(
                                g.source_type
                              )}`}
                            >
                              {moduleLabel(g.source_type, t)}
                            </span>
                            <span
                              className="min-w-0 flex-1 text-sm font-semibold leading-snug text-slate-800"
                              title={g.label}
                            >
                              {g.label}
                            </span>
                            {(g.source_type === "work_order" || g.source_type === "report") &&
                            g.execution_on ? (
                              <span className="shrink-0 text-xs text-slate-500">
                                {formatExecutionOn(g.execution_on, i18n.language)}
                              </span>
                            ) : null}
                            <Link
                              to={sourcePath(g.source_type, g.source_id)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="shrink-0 text-xs font-semibold text-slate-600 underline decoration-slate-300 underline-offset-2 hover:text-slate-900"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {t("inventory.picking.open_document_short")}
                            </Link>
                          </div>
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="text-base font-semibold text-slate-950">
                              {line.product?.title || line.title}
                            </span>
                            {line.product?.item_number ? (
                              <span className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-medium text-slate-600">
                                {line.product.item_number}
                              </span>
                            ) : null}
                            {!warehouseTracked ? (
                              <span
                                className="rounded-full border border-amber-300 bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-900"
                                title={t("inventory.picking.non_warehouse_hint")}
                              >
                                {t("inventory.picking.non_warehouse_badge")}
                              </span>
                            ) : null}
                            {line.skip_picking ? (
                              <span className="rounded-full border border-slate-300 bg-white px-3 py-1 text-xs font-semibold text-slate-700">
                                {t("inventory.picking.skip.badge")}
                              </span>
                            ) : null}
                          </div>
                          <p className="mt-2 text-sm leading-6 text-slate-600">
                            {t("inventory.picking.qty")}: {line.quantity}
                            {line.quantity_picked != null && line.quantity_picked !== line.quantity ? (
                              <span>
                                {" "}
                                · {t("inventory.picking.qty_picked")}: {line.quantity_picked}
                              </span>
                            ) : null}
                          </p>
                          <div className="mt-3 flex flex-wrap items-center gap-2">
                            <span className="inline-flex rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold leading-5 text-slate-700">
                              {lineStatusLabel(line, t)}
                            </span>
                            {line.storage?.title ? (
                              <span className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs text-slate-600">
                                {t("inventory.picking.storage")}: {line.storage.title}
                              </span>
                            ) : null}
                          </div>
                        </div>

                        <div className="flex w-full flex-col gap-3 lg:w-auto lg:max-w-md lg:flex-row lg:items-end lg:justify-end">
                          {line.skip_picking ? (
                            <p className="max-w-xs text-xs leading-relaxed text-slate-600 lg:text-right">
                              {t("inventory.picking.skip.row_hint")}
                            </p>
                          ) : (
                            storageSelect(line)
                          )}
                          <div className="flex items-center justify-end gap-2 lg:shrink-0">
                            <button
                              type="button"
                              title={t("inventory.picking.quick_print.tooltip")}
                              disabled={!line.product_id || rowBusy}
                              onClick={() => onQuickLabel(line.product_id)}
                              className="inline-flex h-11 w-11 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
                            >
                              <PrinterIcon className="h-5 w-5" />
                            </button>
                            <label
                              className={`inline-flex items-center gap-2 rounded-2xl border px-3 py-2 text-sm font-medium ${
                                line.skip_picking
                                  ? "cursor-not-allowed border-slate-100 bg-slate-50 text-slate-400"
                                  : "cursor-pointer border-slate-200 bg-slate-50 text-slate-800"
                              }`}
                              title={
                                line.skip_picking
                                  ? t("inventory.picking.skip.blocks_pick")
                                  : t("inventory.picking.toggle_picked")
                              }
                            >
                              <input
                                type="checkbox"
                                className="h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-400 disabled:opacity-50"
                                checked={Boolean(line.picked)}
                                disabled={rowBusy || line.skip_picking}
                                onChange={(e) => onTogglePicked(line, e.target.checked, g)}
                              />
                              <span className="hidden sm:inline">{t("inventory.picking.picked_label")}</span>
                            </label>
                          </div>
                        </div>
                        </div>
                        <div className="mt-3 flex justify-end border-t border-slate-100/90 pt-2">
                          <label
                            className="inline-flex cursor-pointer items-center gap-1.5 text-[11px] font-normal leading-tight text-slate-500 hover:text-slate-700"
                            title={t("inventory.picking.skip.label")}
                          >
                            <input
                              type="checkbox"
                              className="h-3 w-3 shrink-0 rounded border-slate-300 text-slate-600 focus:ring-slate-400"
                              checked={Boolean(line.skip_picking)}
                              disabled={rowBusy}
                              onChange={(e) => onToggleSkipPicking(line, e.target.checked, g)}
                            />
                            <span>{t("inventory.picking.skip.label")}</span>
                          </label>
                        </div>
                      </div>
                    );
                    })}
                  </div>
                </details>
              ))}
            </div>
          ) : null}
        </section>
      </div>
    </div>
  );
}
